import { useState, useEffect } from 'react';
import { useSeoMeta } from '@unhead/react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { ArrowLeft, Save, Plus, Trash2, GripVertical, Eye, Edit } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Layout } from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useArticle } from '@/hooks/useArticles';
import { usePublishArticle } from '@/hooks/usePublishArticle';
import { CATEGORIES, AUTHOR_PUBKEY } from '@/lib/categories';
import type { QuizQuestion, ArticleFormData } from '@/lib/types';

function QuizQuestionEditor({
  question,
  index,
  onChange,
  onRemove,
}: {
  question: QuizQuestion;
  index: number;
  onChange: (q: QuizQuestion) => void;
  onRemove: () => void;
}) {
  const updateOption = (optionIndex: number, value: string) => {
    const newOptions = [...question.options];
    newOptions[optionIndex] = value;
    onChange({ ...question, options: newOptions });
  };

  const addOption = () => {
    onChange({ ...question, options: [...question.options, ''] });
  };

  const removeOption = (optionIndex: number) => {
    if (question.options.length <= 2) return;
    const newOptions = question.options.filter((_, i) => i !== optionIndex);
    const newCorrectIndex =
      optionIndex === question.correctIndex
        ? 0
        : optionIndex < question.correctIndex
        ? question.correctIndex - 1
        : question.correctIndex;
    onChange({ ...question, options: newOptions, correctIndex: newCorrectIndex });
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-muted-foreground" />
            <CardTitle className="text-base">Frage {index + 1}</CardTitle>
          </div>
          <Button variant="ghost" size="sm" onClick={onRemove} className="text-destructive">
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Frage</Label>
          <Input
            value={question.question}
            onChange={(e) => onChange({ ...question, question: e.target.value })}
            placeholder="Wie lautet die Frage?"
          />
        </div>

        <div className="space-y-2">
          <Label>Antwortmöglichkeiten</Label>
          {question.options.map((option, optionIndex) => (
            <div key={optionIndex} className="flex items-center gap-2">
              <input
                type="radio"
                name={`correct-${index}`}
                checked={question.correctIndex === optionIndex}
                onChange={() => onChange({ ...question, correctIndex: optionIndex })}
                className="w-4 h-4 accent-primary"
              />
              <Input
                value={option}
                onChange={(e) => updateOption(optionIndex, e.target.value)}
                placeholder={`Option ${optionIndex + 1}`}
                className="flex-1"
              />
              {question.options.length > 2 && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => removeOption(optionIndex)}
                  className="text-muted-foreground hover:text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
          <Button variant="outline" size="sm" onClick={addOption} className="mt-2 gap-2">
            <Plus className="w-4 h-4" />
            Option hinzufügen
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Wähle die richtige Antwort mit dem Radio-Button aus.
        </p>
      </CardContent>
    </Card>
  );
}

export function EditorPage() {
  const { slug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { data: existingArticle, isLoading: isLoadingArticle } = useArticle(slug || '');
  const { mutateAsync: publishArticle, isPending: isPublishing } = usePublishArticle();

  const isAuthor = user?.pubkey === AUTHOR_PUBKEY;
  const isEditing = !!slug;

  const [formData, setFormData] = useState<ArticleFormData>({
    title: '',
    summary: '',
    content: '',
    image: '',
    category: 'grundlagen',
    quiz: [],
  });

  const [previewTab, setPreviewTab] = useState<string>('edit');

  // Load existing article data
  useEffect(() => {
    if (existingArticle) {
      setFormData({
        title: existingArticle.title,
        summary: existingArticle.summary,
        content: existingArticle.content,
        image: existingArticle.image || '',
        category: existingArticle.category,
        quiz: existingArticle.quiz || [],
      });
    }
  }, [existingArticle]);

  useSeoMeta({
    title: isEditing ? 'Artikel bearbeiten - wtfisBitcoin' : 'Neuer Artikel - wtfisBitcoin',
    description: 'Artikel für wtfisBitcoin erstellen oder bearbeiten.',
  });

  if (!user) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <h1 className="text-2xl font-bold mb-4">Nicht eingeloggt</h1>
          <p className="text-muted-foreground">
            Du musst eingeloggt sein, um Artikel zu erstellen.
          </p>
        </div>
      </Layout>
    );
  }

  if (!isAuthor) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: 'Fehler',
        description: 'Bitte gib einen Titel ein.',
        variant: 'destructive',
      });
      return;
    }

    if (!formData.content.trim()) {
      toast({
        title: 'Fehler',
        description: 'Bitte gib einen Inhalt ein.',
        variant: 'destructive',
      });
      return;
    }

    try {
      const { slug: newSlug } = await publishArticle({
        ...formData,
        existingSlug: isEditing ? slug : undefined,
      });

      toast({
        title: 'Erfolg!',
        description: isEditing ? 'Artikel wurde aktualisiert.' : 'Artikel wurde veröffentlicht.',
      });

      navigate(`/artikel/${newSlug}`);
    } catch (error) {
      toast({
        title: 'Fehler',
        description: 'Artikel konnte nicht veröffentlicht werden.',
        variant: 'destructive',
      });
    }
  };

  const addQuizQuestion = () => {
    setFormData({
      ...formData,
      quiz: [
        ...formData.quiz,
        {
          question: '',
          options: ['', '', '', ''],
          correctIndex: 0,
        },
      ],
    });
  };

  const updateQuizQuestion = (index: number, question: QuizQuestion) => {
    const newQuiz = [...formData.quiz];
    newQuiz[index] = question;
    setFormData({ ...formData, quiz: newQuiz });
  };

  const removeQuizQuestion = (index: number) => {
    setFormData({
      ...formData,
      quiz: formData.quiz.filter((_, i) => i !== index),
    });
  };

  if (isEditing && isLoadingArticle) {
    return (
      <Layout>
        <div className="container py-16 text-center">
          <p className="text-muted-foreground">Artikel wird geladen...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container py-8 max-w-4xl">
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Zurück
          </button>
          <h1 className="text-3xl font-bold">
            {isEditing ? 'Artikel bearbeiten' : 'Neuer Artikel'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <Card>
            <CardHeader>
              <CardTitle>Grundlegende Informationen</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="title">Titel *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Der Titel deines Artikels"
                />
              </div>

              <div>
                <Label htmlFor="summary">Zusammenfassung</Label>
                <Textarea
                  id="summary"
                  value={formData.summary}
                  onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                  placeholder="Eine kurze Zusammenfassung (optional)"
                  rows={2}
                />
              </div>

              <div>
                <Label htmlFor="category">Kategorie</Label>
                <Select
                  value={formData.category}
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((cat) => (
                      <SelectItem key={cat.id} value={cat.tag}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="image">Titelbild URL (optional)</Label>
                <Input
                  id="image"
                  value={formData.image}
                  onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                  placeholder="https://example.com/bild.jpg"
                />
              </div>
            </CardContent>
          </Card>

          {/* Content */}
          <Card>
            <CardHeader>
              <CardTitle>Inhalt</CardTitle>
            </CardHeader>
            <CardContent>
              <Tabs value={previewTab} onValueChange={setPreviewTab}>
                <TabsList className="mb-4">
                  <TabsTrigger value="edit" className="gap-2">
                    <Edit className="w-4 h-4" />
                    Bearbeiten
                  </TabsTrigger>
                  <TabsTrigger value="preview" className="gap-2">
                    <Eye className="w-4 h-4" />
                    Vorschau
                  </TabsTrigger>
                </TabsList>
                <TabsContent value="edit">
                  <Textarea
                    value={formData.content}
                    onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                    placeholder="Schreibe deinen Artikel hier... (Markdown wird unterstützt)"
                    rows={20}
                    className="font-mono"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Markdown wird unterstützt: **fett**, *kursiv*, # Überschriften, - Listen, etc.
                  </p>
                </TabsContent>
                <TabsContent value="preview">
                  <div className="prose prose-lg dark:prose-invert max-w-none min-h-[400px] p-4 border rounded-lg bg-background">
                    {formData.content ? (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{formData.content}</ReactMarkdown>
                    ) : (
                      <p className="text-muted-foreground italic">Kein Inhalt vorhanden...</p>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Quiz */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Quiz-Fragen</CardTitle>
                <Button type="button" variant="outline" onClick={addQuizQuestion} className="gap-2">
                  <Plus className="w-4 h-4" />
                  Frage hinzufügen
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {formData.quiz.length === 0 ? (
                <p className="text-muted-foreground text-center py-8">
                  Noch keine Quiz-Fragen. Füge Fragen hinzu, um das Wissen der Leser zu testen.
                </p>
              ) : (
                <div className="space-y-4">
                  {formData.quiz.map((question, index) => (
                    <QuizQuestionEditor
                      key={index}
                      question={question}
                      index={index}
                      onChange={(q) => updateQuizQuestion(index, q)}
                      onRemove={() => removeQuizQuestion(index)}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate(-1)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={isPublishing} className="gap-2">
              <Save className="w-4 h-4" />
              {isPublishing ? 'Wird veröffentlicht...' : isEditing ? 'Aktualisieren' : 'Veröffentlichen'}
            </Button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
